import React, { useEffect, useState } from "react";
import axios from "axios";
import { base_url } from "../../utils/axiosConfig";
import { 
  IoSearch, 
  IoDownloadOutline, 
  IoFilterOutline,
  IoCalendarOutline,
  IoChevronBack,
  IoChevronForward
} from "react-icons/io5";
import { 
  BsBoxSeam, 
  BsCheckCircleFill, 
  BsClockFill, 
  BsXCircleFill,
  BsArrowCounterclockwise
} from "react-icons/bs";
import { TbRefresh } from "react-icons/tb";
import { motion, AnimatePresence } from "framer-motion";
import toast from "react-hot-toast";
import jsPDF from "jspdf";
import "jspdf-autotable";
import './DeliveryBoy.css';

/* ─── Helpers ──────────────────────────────── */
const getBadgeClass = (status) => {
  if (status === "Delivered")        return "db-status-badge badge-delivered";
  if (status === "Return")           return "db-status-badge badge-return";
  if (status === "Out for Delivery") return "db-status-badge badge-ofd";
  if (status === "Pending")          return "db-status-badge badge-pending";
  return "db-status-badge badge-default";
};

const PAGE_SIZE = 10;

const Reports = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState("All");
  const [dateRange, setDateRange] = useState("All Time"); // All Time, Today, This Week, This Month

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const raw = localStorage.getItem("customer");
      if (!raw) return toast.error("Not authenticated");
      const { _id: userId } = JSON.parse(raw);
      if (!userId) return toast.error("Invalid session");
      
      const res = await axios.get(`${base_url}orders/delvery-boy/orders/${userId}`);
      setOrders(res.data.orders || []);
    } catch {
      toast.error("Failed to load report data ❌");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  /* ── Filter Logic ── */
  const filtered = orders.filter((o) => {
    const q = search.toLowerCase();
    const textMatch =
      o.trackingNumber?.toLowerCase().includes(q) ||
      o.shippingInfo?.firstName?.toLowerCase().includes(q) ||
      o.shippingInfo?.lastName?.toLowerCase().includes(q) ||
      o.shippingInfo?.city?.toLowerCase().includes(q) ||
      o.orderStatus?.toLowerCase().includes(q);

    const statusMatch = statusFilter === "All" || o.orderStatus === statusFilter;

    // Date filtering
    let dateMatch = true;
    const orderDate = new Date(o.createdAt);
    const now = new Date();
    
    if (dateRange === "Today") {
      dateMatch = orderDate.toDateString() === now.toDateString();
    } else if (dateRange === "This Week") {
      const weekAgo = new Date();
      weekAgo.setDate(now.getDate() - 7);
      dateMatch = orderDate >= weekAgo;
    } else if (dateRange === "This Month") {
      dateMatch = orderDate.getMonth() === now.getMonth() && orderDate.getFullYear() === now.getFullYear();
    }

    return textMatch && statusMatch && dateMatch;
  });

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  /* ── Stats Calculation ── */
  const stats = {
    total: filtered.length,
    delivered: filtered.filter(o => o.orderStatus === "Delivered").length,
    pending: filtered.filter(o => o.orderStatus === "Out for Delivery" || o.orderStatus === "Pending").length,
    returned: filtered.filter(o => o.orderStatus === "Return").length,
    cancelled: filtered.filter(o => o.orderStatus === "Cancelled").length,
    earnings: filtered
      .filter(o => o.orderStatus === "Delivered")
      .reduce((acc, o) => acc + (o.totalPrice || 0), 0)
  };

  /* ── PDF Export ── */
  const exportPDF = () => {
    const doc = new jsPDF();
    const customer = JSON.parse(localStorage.getItem("customer"));
    
    doc.setFontSize(20);
    doc.text("Delivery Boy Performance Report", 14, 22);
    
    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text(`Generated for: ${customer?.firstname} ${customer?.lastname}`, 14, 30);
    doc.text(`Date: ${new Date().toLocaleString()}`, 14, 35);
    doc.text(`Range: ${dateRange} | Status: ${statusFilter}`, 14, 40);

    // Summary stats
    doc.setFontSize(12);
    doc.setTextColor(0);
    doc.text("Summary Stats:", 14, 50);
    doc.autoTable({
      startY: 53,
      head: [["Total Orders", "Delivered", "Returned", "Cancelled", "Total Amount"]],
      body: [[stats.total, stats.delivered, stats.returned, stats.cancelled, `Rs. ${stats.earnings}`]],
    });

    // Detailed Table
    doc.text("Order Details:", 14, doc.lastAutoTable.finalY + 15);
    const tableData = filtered.map((o, i) => [
      i + 1,
      o.trackingNumber?.toUpperCase() || "N/A",
      `${o.shippingInfo?.firstName} ${o.shippingInfo?.lastName}`,
      o.shippingInfo?.city,
      o.totalPrice,
      o.orderStatus,
      new Date(o.createdAt).toLocaleDateString()
    ]);

    doc.autoTable({
      startY: doc.lastAutoTable.finalY + 18,
      head: [["S.No", "Order ID", "Customer", "City", "Amount", "Status", "Date"]],
      body: tableData,
    });

    doc.save(`Delivery_Report_${new Date().getTime()}.pdf`);
    toast.success("Report exported successfully! 📄");
  };

  return (
    <div className="db-reports-container">
      {/* Page Header */}
      <div className="db-page-header d-flex flex-wrap align-items-center justify-content-between gap-3">
        <div>
          <h1 className="db-page-title">Delivery Reports</h1>
          <p className="db-page-subtitle">Analyze your performance and historical data.</p>
        </div>
        <div className="d-flex gap-2">
          <button className="db-btn-ghost" onClick={fetchOrders} title="Refresh Data">
            <TbRefresh size={18} />
          </button>
          <button className="db-btn-primary d-flex align-items-center gap-2" onClick={exportPDF}>
            <IoDownloadOutline size={18} /> Export PDF
          </button>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="row g-3 mb-4">
        {[
          { label: "Delivered", value: stats.delivered, icon: <BsCheckCircleFill />, color: "db-stat-icon-success" },
          { label: "Returned", value: stats.returned, icon: <BsArrowCounterclockwise />, color: "db-stat-icon-danger" },
          { label: "Pending", value: stats.pending, icon: <BsClockFill />, color: "db-stat-icon-warning" },
          { label: "Total Revenue", value: `₹${stats.earnings.toLocaleString()}`, icon: <BsBoxSeam />, color: "db-stat-icon-primary" },
        ].map((s, i) => (
          <div key={i} className="col-6 col-md-3">
            <motion.div 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
              className="db-stat-card"
            >
              <div className={`db-stat-icon ${s.color}`}>{s.icon}</div>
              <div className="db-stat-label">{s.label}</div>
              <div className="db-stat-value" style={{ fontSize: typeof s.value === 'string' ? '1.25rem' : '1.5rem' }}>
                {s.value}
              </div>
            </motion.div>
          </div>
        ))}
      </div>

      {/* Filters Card */}
      <motion.div 
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="db-card mb-4"
      >
        <div className="db-action-bar flex-wrap gap-3">
          <div className="db-search-wrap" style={{ flex: '1 1 300px' }}>
            <IoSearch className="db-search-icon" />
            <input
              type="text"
              placeholder="Search by ID, customer, city..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              className="db-search-input form-control"
            />
          </div>

          <div className="d-flex gap-3 flex-wrap align-items-center">
            <div className="d-flex align-items-center gap-2">
              <IoFilterOutline className="text-secondary" />
              <select 
                className="form-select db-select" 
                value={statusFilter}
                onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
              >
                <option value="All">All Status</option>
                <option value="Delivered">Delivered</option>
                <option value="Return">Returned</option>
                <option value="Out for Delivery">Out for Delivery</option>
                <option value="Cancelled">Cancelled</option>
              </select>
            </div>

            <div className="d-flex align-items-center gap-2">
              <IoCalendarOutline className="text-secondary" />
              <select 
                className="form-select db-select"
                value={dateRange}
                onChange={(e) => { setDateRange(e.target.value); setPage(1); }}
              >
                <option value="All Time">All Time</option>
                <option value="Today">Today</option>
                <option value="This Week">Last 7 Days</option>
                <option value="This Month">This Month</option>
              </select>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Main Data Table */}
      <motion.div 
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="db-card"
      >
        <div className="db-card-header d-flex justify-content-between align-items-center">
          <h2 className="db-card-title">Detailed Report Data</h2>
          <span className="badge bg-light text-dark border">{filtered.length} Records Found</span>
        </div>

        {loading ? (
          <div className="text-center py-5">
            <div className="db-spinner mx-auto mb-3" />
            <p className="text-secondary fw-bold">Generating report...</p>
          </div>
        ) : paginated.length === 0 ? (
          <div className="db-empty-state py-5">
            <div style={{ fontSize: 48, marginBottom: 16 }}>📊</div>
            <h3 className="db-empty-state-title">No data matches your filters</h3>
            <p className="db-empty-state-sub">Try adjusting your filters or search query.</p>
          </div>
        ) : (
          <div className="table-responsive">
            <table className="db-table">
              <thead>
                <tr>
                  <th>S.No</th>
                  <th>Order ID</th>
                  <th>Date</th>
                  <th>Customer</th>
                  <th>City</th>
                  <th>Amount</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {paginated.map((o, idx) => (
                  <tr key={o._id}>
                    <td>{(page - 1) * PAGE_SIZE + idx + 1}</td>
                    <td className="fw-bold text-uppercase" style={{ fontSize: 12 }}>
                      #{o.trackingNumber?.slice(-8) || "N/A"}
                    </td>
                    <td style={{ fontSize: 12 }}>
                      {new Date(o.createdAt).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' })}
                    </td>
                    <td>
                      <div className="fw-bold" style={{ fontSize: 13 }}>{o.shippingInfo?.firstName} {o.shippingInfo?.lastName}</div>
                    </td>
                    <td style={{ fontSize: 12, color: 'var(--db-text-secondary)' }}>{o.shippingInfo?.city}</td>
                    <td className="fw-bold text-primary">₹{o.totalPrice}</td>
                    <td>
                      <span className={getBadgeClass(o.orderStatus)}>{o.orderStatus}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="db-pagination p-3 border-top">
            <span className="d-none d-sm-inline text-secondary" style={{ fontSize: 13 }}>
              Showing <b>{(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filtered.length)}</b> of <b>{filtered.length}</b>
            </span>
            <div className="d-flex gap-1 align-items-center">
              <button 
                className="db-page-btn" 
                disabled={page === 1} 
                onClick={() => { setPage(p => p - 1); window.scrollTo(0,0); }}
              >
                <IoChevronBack size={14} />
              </button>
              {[...Array(totalPages)].map((_, i) => {
                const pg = i + 1;
                if (pg === 1 || pg === totalPages || (pg >= page - 1 && pg <= page + 1)) {
                  return (
                    <button
                      key={pg}
                      className={`db-page-btn ${page === pg ? "active" : ""}`}
                      onClick={() => { setPage(pg); window.scrollTo(0,0); }}
                    >
                      {pg}
                    </button>
                  );
                } else if (pg === page - 2 || pg === page + 2) {
                  return <span key={pg} className="px-1 text-secondary">...</span>;
                }
                return null;
              })}
              <button 
                className="db-page-btn" 
                disabled={page === totalPages} 
                onClick={() => { setPage(p => p + 1); window.scrollTo(0,0); }}
              >
                <IoChevronForward size={14} />
              </button>
            </div>
          </div>
        )}
      </motion.div>

      <style>{`
        .db-reports-container {
          padding-bottom: 2rem;
        }
        .db-select {
          font-size: 13px;
          font-weight: 600;
          border-radius: 8px;
          border: 1.5px solid var(--db-border);
          padding: 6px 30px 6px 12px;
          cursor: pointer;
          background-color: white;
        }
        .db-select:focus {
          border-color: var(--db-primary);
          box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.1);
        }
          
        .db-stat-icon-success { background: rgba(16, 185, 129, 0.1); color: #10b981; }
        .db-stat-icon-danger { background: rgba(239, 68, 68, 0.1); color: #ef4444; }
        .db-stat-icon-warning { background: rgba(245, 158, 11, 0.1); color: #f59e0b; }
        .db-stat-icon-primary { background: rgba(79, 70, 229, 0.1); color: #4f46e5; }
      `}</style>
    </div>
  );
};

export default Reports;
